import { Button } from 'admination-react'
import { DateField } from '@/core/components/form/DateField'
import { useApp } from '@/core/state/appStore'
import { isActive as isCoachingViewFilterActive } from '@/core/lib/coachingFilter'
import type { DashboardCoaching, Stage, StatusFilter } from '@/core/types'
import type { FilterMenuRow } from './FilterMenu'
import { descriptionFor } from '@/variants'
import { currentVariant } from '@/core/data/session'
import { useCoachingFilterCatalogs } from './coachingViewConfig'

export function useCoachingFilterRows(
  coachings: DashboardCoaching[],
  visibleStages: readonly Stage[],
  viewType: 'board' | 'list',
  zurueckgezogen: readonly DashboardCoaching[],
  completeItems: readonly DashboardCoaching[],
): { rows: FilterMenuRow[]; active: boolean } {
  const { state, dispatch } = useApp()
  const isCoachingView = viewType === 'list'
  const catalogs = useCoachingFilterCatalogs(completeItems)

  const { columns: COLUMNS, rueckzug } = descriptionFor(currentVariant())

  const viewFilter = state.coachingViewFilter
  const { excludedCoaches, excludedKostentraeger, datumVon, datumBis } = viewFilter
  const toggleIn = (list: string[], value: string) =>
    list.includes(value) ? list.filter((v) => v !== value) : [...list, value]

  const hiddenStages = state.dashboardHiddenStages
  const toggleColumn = (stage: StatusFilter) => {
    const stages = hiddenStages.includes(stage) ? hiddenStages.filter((s) => s !== stage) : [...hiddenStages, stage]
    dispatch({ type: 'SET_CONTROL', key: 'dashboardHiddenStages', value: stages })
  }

  const stageOptions = [
    { value: 'alle', label: "Alle", count: coachings.length },
    ...COLUMNS.filter((c) => visibleStages.includes(c.id)).map((c) => ({
      value: c.id as string,
      label: c.dbName,
      count: coachings.filter((co) => co.stage === c.id).length,
    })),
    ...(rueckzug ? [{ value: 'zurueckgezogen', label: "Zurückgezogen", count: zurueckgezogen.length }] : []),
  ]
  const currentStageValue = state.coachingFilter ?? 'alle'

  const primaryRow: FilterMenuRow = isCoachingView
    ? {
        kind: 'radio',
        id: 'primary',
        label: "Status",
        options: stageOptions,
        current: currentStageValue,
        onSelect: (value) =>
          dispatch({ type: 'SET_CONTROL', key: 'coachingFilter', value: value === 'alle' ? null : (value as StatusFilter) }),
      }
    : {
        kind: 'options',
        id: 'primary',
        label: "Status",

        options: [
          ...COLUMNS.filter((c) => !c.listOnly && visibleStages.includes(c.id)).map((c) => ({
            key: c.id as string,
            label: c.dbName,
          })),
          ...(rueckzug ? [{ key: 'zurueckgezogen', label: "Zurückgezogen" }] : []),
        ],
        excluded: hiddenStages,
        onToggle: (value) => toggleColumn(value as StatusFilter),

        sorted: false,
      }

  const rows: FilterMenuRow[] = [
    primaryRow,
    {
      kind: 'options',
      id: 'coach',
      label: "Coach",
      options: catalogs.coach,
      excluded: excludedCoaches,
      count: excludedCoaches.length,
      onToggle: (key) =>
        dispatch({ type: 'SET_COACHING_VIEW_FILTER', filter: { excludedCoaches: toggleIn(excludedCoaches, key) } }),
    },
    {
      kind: 'options',
      id: 'kostentraeger',
      label: "Kostenträger",
      options: catalogs.kostentraeger,
      excluded: excludedKostentraeger,
      count: excludedKostentraeger.length,
      onToggle: (key) =>
        dispatch({
          type: 'SET_COACHING_VIEW_FILTER',
          filter: { excludedKostentraeger: toggleIn(excludedKostentraeger, key) },
        }),
    },
    {
      kind: 'custom',
      id: 'datum',
      label: "Datum",
      count: (datumVon ? 1 : 0) + (datumBis ? 1 : 0),
      stacked: true,
      render: () => (
        <>
          <DateField
            label="Von"
            isoValue={datumVon ?? ''}
            maxIso={datumBis ?? undefined}
            onChange={(iso) => dispatch({ type: 'SET_COACHING_VIEW_FILTER', filter: { datumVon: iso || null } })}
          />
          <DateField
            label="Bis"
            isoValue={datumBis ?? ''}
            minIso={datumVon ?? undefined}
            onChange={(iso) => dispatch({ type: 'SET_COACHING_VIEW_FILTER', filter: { datumBis: iso || null } })}
          />
          {(datumVon !== null || datumBis !== null) && (
            <Button
              type="button"
              variant="filter-reset"
              onClick={() => dispatch({ type: 'SET_COACHING_VIEW_FILTER', filter: { datumVon: null, datumBis: null } })}
            >
              Zurücksetzen</Button>
          )}
        </>
      ),
    },
  ]

  return { rows, active: isCoachingViewFilterActive(viewFilter) }
}
